import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/clientWeb/components/ui/dialog'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

interface MicPermissionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onTryAgain: () => void
  /**
   * Which device the browser refused. Defaults to the microphone, which is all
   * this dialog used to be able to talk about — a blocked camera got the
   * microphone's wording, telling people to look for "Microphone" in a
   * permissions list while their camera was the thing that was off.
   */
  kind?: 'audio' | 'video'
}

export function MicPermissionDialog({
  open,
  onOpenChange,
  onTryAgain,
  kind = 'audio',
}: MicPermissionDialogProps) {
  const t = useTranslation()
  // Only the three device-specific lines differ; the unblock steps themselves
  // are the same walk through the same browser menu.
  const copy =
    kind === 'video'
      ? {
          title: 'dashboard.cameraAccessBlocked',
          description: 'dashboard.cameraAccessBlockedDesc',
          toEnable: 'dashboard.toEnableCameraAccess',
          findPermission: 'dashboard.findCameraPermission',
        }
      : {
          title: 'dashboard.micAccessBlocked',
          description: 'dashboard.micAccessBlockedDesc',
          toEnable: 'dashboard.toEnableMicAccess',
          findPermission: 'dashboard.findMicPermission',
        }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(copy.title)}</DialogTitle>
          <DialogDescription>{t(copy.description)}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <div className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-amber-600 text-xs font-bold">!</span>
              </div>
              <div className="text-sm text-amber-800">
                <p className="font-medium">{t(copy.toEnable)}</p>
                <ol className="mt-1 list-decimal list-inside space-y-1 text-xs">
                  <li>{t('dashboard.clickLockIcon')}</li>
                  <li>{t(copy.findPermission)}</li>
                  <li>{t('dashboard.changeBlockToAllow')}</li>
                  <li>{t('dashboard.refreshPage')}</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-lg border border-slate-200 dark:border-border px-4 py-2 text-sm font-medium text-slate-700 dark:text-foreground hover:bg-slate-50 dark:hover:bg-muted transition"
            >
              {t('dashboard.illDoItLater')}
            </button>
            <button
              type="button"
              onClick={() => {
                onOpenChange(false)
                onTryAgain()
              }}
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition"
            >
              {t('dashboard.tryAgain')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
